import React from 'react';
import { BookOpen, Users, Award, Zap, Target, TrendingUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const About = () => {
  const highlights = [
    {
      icon: <BookOpen size={32} />,
      title: 'Research Excellence',
      description: '44 international journal publications and 50+ conference/conclave papers',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: <Users size={32} />,
      title: 'Student Mentorship',
      description: 'Supervised 7 PhD students, currently supervising 6 research scholars and B.Tech students',
      color: 'from-cyan-500 to-purple-500',
      bgColor: 'bg-cyan-50',
    },
    {
      icon: <Award size={32} />,
      title: 'Industry Collaboration',
      description: 'Collaborated with ISRO, DRDO, TATA Materials, SABIC, and other leading organizations',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
    },
  ];

  const achievements = [
    { icon: <Zap size={24} />, text: 'PhD from IIT Bombay-Monash Research Academy', color: 'text-blue-600' },
    { icon: <Target size={24} />, text: 'Significant research funding from DST-SERB, ISRO, DRDO', color: 'text-cyan-600' },
    { icon: <TrendingUp size={24} />, text: 'Multiple Department Best Teacher Awards', color: 'text-purple-600' },
  ];

  return (
    <section id="about" className="section-container bg-gradient-to-br from-white via-blue-50 to-cyan-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full filter blur-3xl opacity-20"></div>
      
      <h2 className="section-title">About</h2>
      
      <div className="w-full mb-16">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-blue-100">
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Dr. Nelson Muthu is an <span className="font-bold text-blue-600">Associate Professor</span> in the Mechanical Engineering Department at the Indian Institute of Technology Palakkad. 
            He previously served as an Assistant Professor at IIT Guwahati from 2017 to 2023, where he established a comprehensive composite 
            manufacturing and testing laboratory.
          </p>
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            With a <span className="font-bold text-cyan-600">PhD from the IIT Bombay-Monash Research Academy</span>, Dr. Muthu specializes in Computational Elasticity, Mechanics of Composites, 
            and Medical Device Innovation. His research focuses on developing advanced numerical methods for fracture mechanics, composite materials 
            testing and manufacturing, and innovative medical device solutions.
          </p>
          <p className="text-lg text-slate-700 leading-relaxed">
            Dr. Muthu has secured significant research funding from prestigious agencies including DST-SERB, ISRO, DRDO, 
            and industry partners. He has authored a book on Strength of Materials and holds multiple patents in medical device innovation.
          </p>
        </div>

        {/* Quick achievements */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {achievements.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-4 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-3"
            >
              <div className={`${item.color}`}>
                {item.icon}
              </div>
              <p className="text-sm font-semibold text-slate-700">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 relative z-10">
        {highlights.map((item, index) => (
          <Card key={index} className="border-2 border-transparent hover:border-blue-200 overflow-hidden group interactive-card">
            <CardContent className="p-8 text-center relative">
              <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
              <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${item.color} rounded-2xl text-white mb-6 shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                {item.icon}
              </div>
              <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-3">
                {item.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">{item.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default About;